import React, { useRef, useState, useEffect, Fragment } from "react";
import { Checkbox, Divider, Flex, Select, Text, Button, Switch } from "@theorgchartdev/storybook";
import RenamePopover from "@/appComponents/Blocks/Composites/RenamePopover/RenamePopover";
import {
  ChartHasRecordDataLoaded,
  ValidateMapping
} from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/Utils";
import { getFeatureAccess, getHoverTipContent } from "@/Utils/Misc";
import { appstr } from "@coreapp/utils/appstr";
import { enumBrand, enumFeaturesList, kReservedFields } from "@coreapp/constants";
import EditableItemGroup from "@/appComponents/Blocks/Utils/EditableItemGroup/EditableItemGroup";
import { SelectableSearchMenu } from "@/appComponents/Blocks/Menus/BasicChartSearchMenu/SelectableSearchMenu";
import { LookupRecord } from "@/Utils/Promises";
import { BoxChip } from "@/Utils/BoxChip";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import UpgradePopover from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradePopover";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { PositionConfig } from "@coreapp/CoreApp/PositionConfig";
import { PLACEHOLDER_TABLE_VALUE } from "@/Utils/Constants";
import { DATA_SOURCES_TEST_PREFIX } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/Utils";
import { gWhiteLabel } from "@coreapp/whitelabel/brand";

export const DataSourceOptionsContent = ({ chartConfig, handleUpdate }) => {
  const [isLoadingRecords, setLoadingRecords] = useState(false);
  const [topBoxRecord, setTopBoxRecord] = useState({
    topBoxID: "",
    topBoxName: "",
    topBoxTitle: "",
    label: ""
  });

  const searchHandleRef = useRef(null);
  const coHeadHandleRef = useRef(null);
  const isPositionChart = IsPositionChart(chartConfig);
  const functionalChartEnabled = getFeatureAccess(enumFeaturesList.functionalChart);

  const { showPricingAndPackaging } = usePermissionCheck();

  const positionConfig = new PositionConfig(chartConfig);
  const isSyntheticPositionChart = isPositionChart && positionConfig.isPositionSynth();
  const gapFillConfig = positionConfig.getGapFillConfig();

  const toggleGapFill = nextState => {
    const newGapFillConfig = {
      enabled: nextState
    };

    updateConfig(newGapFillConfig);
  };

  const toggleAutoPurge = nextState => {
    const newGapFillConfig = {
      autoPurgeEnabled: nextState
    };

    updateConfig(newGapFillConfig);
  };

  const handleAgeOutDaysChange = option => {
    const newGapFillConfig = {
      ageOutDays: parseInt(option.value, 10)
    };

    updateConfig(newGapFillConfig);
  };

  const toggleSubRequirement = nextState => {
    const newGapFillConfig = {
      subThreshold: nextState ? 1 : 0
    };
    updateConfig(newGapFillConfig);
  };

  const updateConfig = updatedGapConfig => {
    const config = new PositionConfig(chartConfig);
    const currentConfig = config.positionConfig;
    const synthConfig = currentConfig.synthConfig || {};
    const currentGapFillConfig = positionConfig.getGapFillConfig();

    const nextGapFillConfig = {
      ...currentGapFillConfig,
      ...updatedGapConfig
    };

    const updatedPositionChart = {
      ...currentConfig,
      synthConfig: {
        ...synthConfig,
        gapFillConfig: nextGapFillConfig
      }
    };

    handleUpdate({ positionChart: updatedPositionChart }, "positionChart");
  };

  const buildAgeOutDaysOptions = () => {
    const options = [];
    const dayOptions = [0, 7, 14, 30, 60, 90];

    dayOptions.forEach(days => {
      options.push({
        label: days === 0 ? appstr("Immediate days") : `${days} days`,
        value: days.toString(),
        clickHandler: handleAgeOutDaysChange
      });
    });

    return options;
  };

  useEffect(() => {
    const LoadTopBoxRecord = async () => {
      if (isLoadingRecords || !chartConfig?.topBoxID || !ValidateMapping(chartConfig)) {
        setTopBoxRecord({
          topBoxID: "",
          topBoxName: "",
          topBoxTitle: "",
          label: ""
        });
        return;
      }

      let record, nameField, titleField, personIDField;
      setLoadingRecords(true);

      LookupRecord([chartConfig.topBoxID], chartConfig?.ID, false, true)
        .then(response => {
          const responseObj = JSON.parse(response);
          // TopBox exists
          if (responseObj.success) {
            record = BoxChip.FromLookupResult(responseObj?.data?.[0], chartConfig);
          } else if (chartConfig.coHeads) {
            // TopBox does not exist, use coheads.
            record = BoxChip.FromCohead(responseObj?.data?.[0]);
          }

          if (record)
            setTopBoxRecord({
              topBoxID: record.entityID ?? "",
              topBoxName: record.name ?? "",
              topBoxTitle: record.title ?? "",
              label: record.label()
            });
        })
        .finally(() => {
          setLoadingRecords(false);
        });
    };

    LoadTopBoxRecord();
  }, [chartConfig]);

  const getCoHeadsLabel = coHead => {
    if (!coHead.Name && !coHead.Title) return "";
    return `${coHead.Name || ""} ${coHead.Title ? `(${coHead.Title})` : ""}`.trim();
  };

  const createPersonObject = data => ({
    PersonID: data.topBoxID || data.PersonID || "",
    Name: data.topBoxName || data.Name || "",
    Title: data.topBoxTitle || data.Title || "",
    treeID: 0,
    "Employee Title": data.topBoxTitle || data.Title || ""
  });

  const updateCoHeadsForTopBox = (oldTopBoxID, newTopBox) => {
    if (!chartConfig.coHeads) return null;
    if (!oldTopBoxID && !newTopBox.topBoxID) return chartConfig.coHeads;

    let coHeadsList = [...chartConfig.coHeads];
    const personFromTopBox = createPersonObject(newTopBox);

    if (newTopBox.topBoxName === "" && newTopBox.topBoxID === "") {
      if (oldTopBoxID) {
        coHeadsList = coHeadsList.filter(ch => ch.PersonID !== oldTopBoxID);
      }
    } else if (newTopBox.topBoxName !== "") {
      if (oldTopBoxID) {
        const existingIndex = coHeadsList.findIndex(ch => ch.PersonID === oldTopBoxID);
        if (existingIndex >= 0) {
          coHeadsList[existingIndex] = personFromTopBox;
        }
      }
      if (
        newTopBox.topBoxID &&
        !coHeadsList.some(person => person.PersonID === newTopBox.topBoxID)
      ) {
        coHeadsList.unshift(personFromTopBox);
      }
    }
    return coHeadsList;
  };

  const updateTopBox = topBoxDict => {
    const newTopBox = {
      ...topBoxDict,
      label: topBoxDict.topBoxName
        ? `${topBoxDict.topBoxName} ${topBoxDict.topBoxTitle} (${topBoxDict.topBoxID})`
        : ""
    };

    const updatedCoHeadsList = updateCoHeadsForTopBox(topBoxRecord.topBoxID, newTopBox);

    const updates = {
      ...topBoxDict,
      ...(updatedCoHeadsList ? { coHeads: updatedCoHeadsList } : {})
    };

    setTopBoxRecord(newTopBox);

    handleUpdate(updates, [...Object.keys(topBoxDict), ...(updatedCoHeadsList ? ["coHeads"] : [])]);
  };

  const updateCoHeads = (coHeadsDict, key, notClear = true) => {
    if (!chartConfig.coHeads) return;

    const updatedCoHeads = [...chartConfig.coHeads];
    const foundPerson = updatedCoHeads.find(coHead => coHead.Name === coHeadsDict.Name);
    const updatedKey = Number(key);

    if (updatedKey >= 0 && updatedKey < updatedCoHeads.length) {
      updatedCoHeads[updatedKey] = coHeadsDict;

      if (!foundPerson || !notClear) {
        handleUpdate(
          {
            coHeads: updatedCoHeads
          },
          "coHeads"
        );
      }
    }
  };

  const clearSearchTarget = target => {
    if (!target || target.id === undefined) return;

    coHeadHandleRef.current?.setValue("", true);
    updateCoHeads(
      createPersonObject({ PersonID: "", Name: "", Title: "" }),
      Number(target.id),
      false
    );
  };

  const toggleFunctionalChart = () => {
    let newFunctionalChart = { ...chartConfig?.functionalChart };

    if (!newFunctionalChart.enabled) {
      newFunctionalChart = {
        enabled: true,
        "Function 1": "",
        "Function 2": "",
        "Function 3": ""
      };
    } else {
      newFunctionalChart.enabled = false;
    }

    handleUpdate(
      {
        functionalChart: newFunctionalChart
      },
      "functionalChart"
    );
  };

  const toggleCoHeadChart = () => {
    const nextIsCoheadsSet = !chartConfig?.doCoHead;
    let nextCoheadsList;

    if (nextIsCoheadsSet) {
      nextCoheadsList = [createPersonObject(topBoxRecord)];
    } else {
      nextCoheadsList = [];
    }

    handleUpdate(
      {
        doCoHead: nextIsCoheadsSet,
        coHeads: nextCoheadsList
      },
      ["doCoHead", "coHeads"]
    );
  };

  const deleteCoHead = index => {
    const updatedCoHeads = [...chartConfig.coHeads];
    if (index >= 0 && index < updatedCoHeads.length) {
      updatedCoHeads.splice(index, 1);

      handleUpdate(
        {
          coHeads: updatedCoHeads
        },
        "coHeads"
      );
    }
  };

  const changeFunction = index => {
    return option => {
      const functionIndex = "Function " + index;
      const newValue = option.value;
      handleUpdate(
        {
          ...chartConfig,
          functionalChart: {
            ...chartConfig.functionalChart,
            [functionIndex]: newValue
          }
        },
        "functionalChart"
      );
    };
  };

  const buildFunctionalChartOptions = index => {
    const allFields = chartConfig?.headers ?? [];

    const formulaFields = chartConfig?.formulas?.map(f => f.name) ?? [];
    const systemFields = kReservedFields + ["objType", "isAssistant"];
    const excludeFields = formulaFields + systemFields;

    const allFieldsMinusFormula = allFields.filter(f => !excludeFields.includes(f));
    const options = [
      {
        label: appstr("No field"),
        value: "",
        clickHandler: changeFunction(index)
      }
    ];

    allFieldsMinusFormula.map(f => {
      options.push({
        label: f,
        value: f,
        clickHandler: changeFunction(index)
      });
    });

    return options;
  };

  const addCoheadToList = () => {
    let coHeadsList = [...(chartConfig?.coHeads || [])];
    if (coHeadsList.length === 0 && topBoxRecord.topBoxName) {
      coHeadsList.push(createPersonObject(topBoxRecord));
    }

    coHeadsList.push(createPersonObject({ PersonID: "", Name: "", Title: "" }));

    handleUpdate(
      {
        coHeads: coHeadsList
      },
      "coHeads"
    );
  };

  const chartHasRecordDataLoaded = ChartHasRecordDataLoaded(chartConfig);
  const isCoHeadChart = chartConfig?.doCoHead;

  const isFunctionalChart = chartConfig?.functionalChart?.enabled;

  const allowTopBoxSearch = chartHasRecordDataLoaded && !isCoHeadChart;
  const allowManualTopBoxIDEntry = !chartHasRecordDataLoaded;
  const coHeadsList = chartConfig?.coHeads ?? [];
  const numCoHeads = coHeadsList.length;
  const disableCoHeadButton = !(isCoHeadChart && numCoHeads <= 3);
  const coHeadsWithIndex = coHeadsList.map((item, index) => ({ ...item, index }));
  const filteredCoHeads = coHeadsWithIndex
    .filter(coHead => coHead.PersonID !== topBoxRecord?.topBoxID)
    .map(coHead => ({
      ...coHead,
      id: coHead.index,
      label: getCoHeadsLabel(coHead)
    }));

  const fcUpgradeMessage = {
    message: appstr("functional chart message"),
    template: appstr("functional chart message"),
    formatConfig: { bold1: { type: "bold" } },
    replaceDict: {}
  };

  const fcPillLabel = appstr(gWhiteLabel === enumBrand.isolved ? "Upgrade" : "Upgrade to create");
  const showGapFillFeature = isSyntheticPositionChart;
  const gapFillEnabled = gapFillConfig.enabled;

  return (
    <Flex direction={"column"} gap={"20px"} width={"100%"}>
      <Flex gap={"8px"} direction={"column"} width={"100%"}>
        <Text size={"small"} modifier={"medium"}>
          {appstr("Top box of chart")}
        </Text>{" "}
        <Flex gap={"8px"} alignItems={"center"}>
          <SelectableSearchMenu
            dataTestId={"co-heads-search"}
            inputSize="medium"
            displayRecents={false}
            selectedItem={topBoxRecord}
            onRemoveItem={() => {
              const emptyTopBox = {
                topBoxID: "",
                topBoxName: "",
                topBoxTitle: "",
                label: ""
              };
              setTopBoxRecord(emptyTopBox);
              updateTopBox(emptyTopBox);
            }}
            searchMenuProps={{
              clearOnSelect: true,
              clearable: true,
              placeholder: topBoxRecord.label ? "" : appstr("Search by name or title"),
              searchProps: {
                handleRef: searchHandleRef
              },
              inputProps: {
                disabled: !allowTopBoxSearch
              },
              tooltipProps: getHoverTipContent("settopbox", "top-start")
            }}
            handleSearchOptionClick={target => {
              let record = target.result;
              searchHandleRef.current?.setValue("", true);
              updateTopBox({
                topBoxID: target.value,
                topBoxName: record.Name,
                topBoxTitle: record.Title
              });
            }}
            queryOptions={{ includeAux: false }}
            queryChartId={chartConfig.ID}
            isPositionChart={isPositionChart}
          />
          {allowManualTopBoxIDEntry && (
            <RenamePopover
              iconProps={{ name: "pen" }}
              name={chartConfig?.topBoxID ?? ""}
              onCommit={newTopBoxID =>
                updateTopBox({
                  topBoxID: newTopBoxID,
                  topBoxName: "",
                  topBoxTitle: ""
                })
              }
              triggerProps={{
                tooltipProps: getHoverTipContent("Set Supervisor", "top-start")
              }}
              inputProps={{
                inputOnly: false,
                label: appstr("Enter person id of top box"),
                placeholder: "eg. 123"
              }}
            />
          )}
        </Flex>
      </Flex>
      {/* Temporarily suppress co heads and functional charts when not position chart */}
      {!isPositionChart && (
        <>
          <Checkbox
            checked={isCoHeadChart}
            data-testid={`${DATA_SOURCES_TEST_PREFIX}-add-co-heads`}
            disabled={!topBoxRecord?.topBoxID && !getFeatureAccess(enumFeaturesList.coHeads)}
            id="cohead-enabled"
            label={appstr("addcoheadscb")}
            onToggle={toggleCoHeadChart}
            size="medium"
          />
          {filteredCoHeads.map((coHead, key) => (
            <Fragment key={key}>
              <EditableItemGroup
                dataTestId={`co-head-${key}!`}
                actions={
                  isCoHeadChart
                    ? [
                        {
                          iconName: "trash-can",
                          iconProps: { color: "error" },
                          onClick: () => deleteCoHead(coHead.index),
                          dataTestId: `${DATA_SOURCES_TEST_PREFIX}-co-head-${key}-delete`
                        }
                      ]
                    : []
                }
              >
                <SelectableSearchMenu
                  dataTestId={"co-heads-search"}
                  inputSize="medium"
                  displayRecents={false}
                  selectedItem={coHead}
                  onRemoveItem={clearSearchTarget}
                  searchMenuProps={{
                    clearOnSelect: true,
                    clearable: true,
                    placeholder: coHead.label ? "" : appstr("Search by name or title"),
                    searchProps: {
                      handleRef: coHeadHandleRef
                    },
                    inputProps: {
                      disabled: !isCoHeadChart
                    }
                  }}
                  handleSearchOptionClick={target => {
                    coHeadHandleRef.current?.setValue("", true);
                    updateCoHeads(
                      {
                        PersonID: target.value,
                        Name: target.result["Name"],
                        Title: target.result["Title"],
                        treeID: 0,
                        "Employee Title": target.result["Title"]
                      },
                      coHead.index
                    );
                  }}
                  queryOptions={{ includeAux: false }}
                />
              </EditableItemGroup>
            </Fragment>
          ))}

          <Flex direction="row">
            <Button
              dataTestId="data-source-create-co-head"
              modifier="secondary"
              className="oc-special-secondary"
              iconProps={{ name: "plus" }}
              disabled={disableCoHeadButton}
              isFullWidth={false}
              onClick={addCoheadToList}
              tooltipProps={{
                message:
                  isCoHeadChart && disableCoHeadButton
                    ? appstr("The system only allows you to add up to three additional co-heads")
                    : appstr("Add co-head")
              }}
            >
              {appstr("Add co-head")}
            </Button>
          </Flex>
          <Divider />
          <Flex direction="row" alignItems="center" gap={"12px"}>
            <Checkbox
              checked={isFunctionalChart}
              data-testid={`${DATA_SOURCES_TEST_PREFIX}-functional-chart-enabled`}
              id="functional-chart-enabled"
              label={appstr("placeholdercb")}
              onToggle={toggleFunctionalChart}
              size="medium"
              disabled={!functionalChartEnabled}
            />
            {showPricingAndPackaging && !functionalChartEnabled && (
              <UpgradePopover
                pillLabel={fcPillLabel}
                popoverText={fcUpgradeMessage}
                featureKey={enumFeaturesList.functionalChart}
              />
            )}
          </Flex>

          {isFunctionalChart && (
            <Flex direction="column" gap="20px" width="400px">
              {[1, 2, 3].map(i => (
                <Fragment key={i}>
                  <Text size="small" modifier="medium">
                    {appstr("functionalchartoption", { index: i })}
                  </Text>
                  <Select
                    dataTestId={`${DATA_SOURCES_TEST_PREFIX}-functional-chart-function-${i}-select`}
                    value={chartConfig.functionalChart[`Function ${i}`]}
                    options={buildFunctionalChartOptions(i)}
                    selectSize="medium"
                    optionSize="medium"
                    listWidth="fill"
                  />
                </Fragment>
              ))}
            </Flex>
          )}
        </>
      )}

      {showGapFillFeature && (
        <>
          <Divider />
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "24px" }}>
            <Flex direction={"column"} gap={"16px"}>
              <div>
                <div style={{ position: "relative" }}>
                  <Switch
                    data-testid={`${DATA_SOURCES_TEST_PREFIX}-retain-vacant-positions-switch`}
                    label={appstr("Retain vacant supervisory positions")}
                    disabled={false}
                    id="retain-vacant-positions-switch"
                    onChange={nextChecked => toggleGapFill(nextChecked)}
                    checked={gapFillConfig.enabled || false}
                  />
                </div>
                <div className={"switch-sub-text-wrapper"}>
                  <Text size={"small"} modifier={"gray"}>
                    {appstr("Retain vacant supervisory positions info")}
                  </Text>
                </div>
              </div>
              <div className={"switch-sub-text-wrapper"}>
                <div style={{ position: "relative" }}>
                  <Switch
                    data-testid={`${DATA_SOURCES_TEST_PREFIX}-retain-only-subs-switch`}
                    label={appstr("Retain only vacant with subs")}
                    disabled={!gapFillConfig.enabled}
                    id="retain-only-subs"
                    onChange={nextChecked => toggleSubRequirement(nextChecked)}
                    checked={gapFillEnabled && gapFillConfig.subThreshold}
                  />
                </div>
              </div>
              <div className={"switch-sub-text-wrapper"}>
                <div style={{ position: "relative" }}>
                  <Switch
                    data-testid={`${DATA_SOURCES_TEST_PREFIX}-clear-vacant-positions-switch`}
                    label={appstr("Clear vacant positions")}
                    disabled={!gapFillConfig.enabled}
                    id="clear-vacant-positions-switch"
                    onChange={nextChecked => toggleAutoPurge(nextChecked)}
                    checked={(gapFillEnabled && gapFillConfig.autoPurgeEnabled) || false}
                  />
                </div>
                <div style={{ marginLeft: "32px" }}>
                  <Text size={"small"} modifier={"gray"}>
                    {appstr("Clear vacant positions after info")}
                  </Text>
                </div>
              </div>
            </Flex>
            <Flex direction={"column"} justifyContent={"flex-end"}>
              <Flex direction={"column"} gap={"8px"} width={"300px"}>
                <Text size={"small"} modifier={"medium"}>
                  {appstr("Clear vacant positions after")}
                </Text>
                <Select
                  dataTestId={`${DATA_SOURCES_TEST_PREFIX}-clear-vacant-positions-age-out-select`}
                  value={gapFillEnabled ? gapFillConfig.ageOutDays?.toString() || "30" : undefined}
                  options={buildAgeOutDaysOptions()}
                  placeholder={PLACEHOLDER_TABLE_VALUE}
                  selectSize="medium"
                  optionSize="medium"
                  listWidth="fill"
                  disabled={!gapFillConfig.enabled || !gapFillConfig.autoPurgeEnabled}
                />
              </Flex>
            </Flex>
          </div>
        </>
      )}
    </Flex>
  );
};
